import type { FC } from 'react';
import { Tooltip } from 'antd';
import { tpoCalendarEventBarTestId } from '../../../config/testIds';
import type { CalendarEvent, Frequency, WeekStartDay } from '../../../types/calendar';
import CalendarInterval from './CalendarInterval';

export interface PeriodCellProps {
    events: CalendarEvent[];
    frequency: Frequency;
    pxPerDay: number;
    periodStartDate: string;
    cellWidth: number;
    /** Actual day span of this period (period_end_date - period_start_date + 1); same value used to derive `pxPerDay`. */
    totalDays: number;
    /** Tenant-configured first day of the week (e.g. Monday for Danone); aligns monthly week dividers to real weeks. */
    weekStartDay: WeekStartDay;
    /** Owning cell's test id; each event bar derives its own id from it. */
    testId?: string;
}

const PeriodCell: FC<PeriodCellProps> = ({
    events,
    frequency,
    pxPerDay,
    periodStartDate,
    cellWidth,
    totalDays,
    weekStartDay,
    testId,
}) => {
    // startDay/durationDays are always day counts, so bar layout needs a pixels-per-day
    // scale. Weekly columns use a fixed 7-day width to stay aligned with the gridlines in
    // CalendarInterval; monthly and quarterly columns vary in day span, so pxPerDay (derived
    // per period) is used instead.
    const marginLeftSpacing = frequency === 'W' ? cellWidth / 7 : pxPerDay;

    return (
        <>
            <CalendarInterval
                frequency={frequency}
                periodStartDate={periodStartDate}
                cellWidth={cellWidth}
                pxPerDay={pxPerDay}
                totalDays={totalDays}
                weekStartDay={weekStartDay}
            />
            <div className="items-center flex">
                {events?.map((evt, idx) => (
                    <Tooltip
                        key={`${periodStartDate}-${evt.startDay}-${idx}`}
                        title={[
                            ...evt.tooltipValues.map((v) => v || 'N/A'),
                            `${Math.round(evt.durationDays / 7)} weeks`,
                        ].join(' | ')}
                    >
                        <div
                            className="absolute z-[1] h-8 border-l-2 flex items-center overflow-hidden text-xs font-normal rounded-tr-[0.25rem] rounded-br-[0.25rem] pointer-events-auto cursor-pointer"
                            style={{
                                marginLeft: evt.startDay * marginLeftSpacing,
                                width: evt.durationDays * marginLeftSpacing,
                                backgroundColor: evt.bgColor,
                                borderColor: evt.color,
                                color: evt.color,
                            }}
                            data-testid={testId ? tpoCalendarEventBarTestId(testId, idx) : undefined}
                            data-state-color-type={evt.colorType || undefined}
                        >
                            <div className="ml-2 min-w-0">
                                <p className="truncate">
                                    {[
                                        ...evt.barLabelValues,
                                        `${Math.round(evt.durationDays / 7)} weeks`,
                                    ].join(' | ')}
                                </p>
                            </div>
                        </div>
                    </Tooltip>
                ))}
            </div>
        </>
    );
};

PeriodCell.displayName = 'PeriodCell';
export default PeriodCell;
